import { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, CheckCircle, AlertTriangle, Clock, Play,
  RefreshCw, Search, Filter, X, ChevronRight, Shield,
  Globe, FileText, Lock, ToggleLeft, ToggleRight, Save
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============================================================================
// CONFIG BADGES
// ============================================================================

const EVENT_STATUS_CONFIG = {
  upcoming: { 
    label: 'In programma', 
    color: 'bg-green-100 text-green-700 border-green-200', 
    icon: Calendar 
  },
  ongoing: { 
    label: 'In corso', 
    color: 'bg-blue-100 text-blue-700 border-blue-200', 
    icon: Play 
  },
  past: { 
    label: 'Concluso', 
    color: 'bg-gray-100 text-gray-500 border-gray-200', 
    icon: CheckCircle 
  },
  to_verify: { 
    label: 'Da verificare', 
    color: 'bg-amber-100 text-amber-700 border-amber-200', 
    icon: AlertTriangle 
  },
};

const SOURCE_TYPE_CONFIG = {
  manual_override: { 
    label: 'Override', 
    color: 'bg-purple-100 text-purple-700 border-purple-200', 
    icon: Shield 
  },
  official: { 
    label: 'Ufficiale', 
    color: 'bg-emerald-100 text-emerald-700 border-emerald-200', 
    icon: CheckCircle 
  },
  curated_portal: { 
    label: 'Portale', 
    color: 'bg-sky-100 text-sky-700 border-sky-200', 
    icon: Globe 
  },
  secondary: { 
    label: 'Secondaria', 
    color: 'bg-slate-100 text-slate-600 border-slate-200', 
    icon: FileText 
  },
  unknown: { 
    label: 'Sconosciuta', 
    color: 'bg-gray-100 text-gray-500 border-gray-200', 
    icon: AlertTriangle 
  },
};

// ============================================================================
// FILTER PILLS
// ============================================================================

const FilterPill = ({ active, onClick, children, count }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 text-sm rounded-full border transition-all flex items-center gap-1.5 ${
      active 
        ? 'bg-umbria-olive text-white border-umbria-olive' 
        : 'bg-white text-gray-600 border-gray-200 hover:border-umbria-olive/50'
    }`}
  >
    {children}
    {count !== undefined && (
      <span className={`text-xs ${active ? 'bg-white/20' : 'bg-gray-100'} px-1.5 py-0.5 rounded-full`}>
        {count}
      </span>
    )}
  </button>
);

// ============================================================================
// EVENT CARD
// ============================================================================

const EventCard = ({ event, onClick }) => {
  const statusConfig = EVENT_STATUS_CONFIG[event.event_status] || EVENT_STATUS_CONFIG.to_verify;
  const sourceConfig = SOURCE_TYPE_CONFIG[event.source_type] || SOURCE_TYPE_CONFIG.unknown;
  const StatusIcon = statusConfig.icon;
  const SourceIcon = sourceConfig.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card 
      className="hover:border-umbria-olive/50 cursor-pointer transition-all hover:shadow-md group"
      onClick={onClick}
      data-testid={`event-card-${event.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          {/* Main Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-umbria-charcoal truncate group-hover:text-umbria-olive transition-colors">
              {event.title}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {event.location || 'Umbria'}
            </p>
            
            {/* Dates */}
            <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
              <Calendar className="h-3.5 w-3.5 text-gray-400" />
              <span>
                {formatDate(event.start_date)}
                {event.end_date && event.end_date !== event.start_date && (
                  <> — {formatDate(event.end_date)}</>
                )}
              </span>
            </div>
          </div>

          {/* Right Side - Badges + Arrow */}
          <div className="flex flex-col items-end gap-2">
            {/* Status Badge */}
            <Badge className={`${statusConfig.color} border text-xs font-normal`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusConfig.label}
            </Badge>
            
            {/* Source Badge */}
            <Badge className={`${sourceConfig.color} border text-xs font-normal`}>
              <SourceIcon className="h-3 w-3 mr-1" />
              {sourceConfig.label}
            </Badge>

            {/* Indicators */}
            <div className="flex gap-1">
              {event.manual_override && (
                <span className="text-purple-600" title="Override manuale attivo">
                  <Lock className="h-3.5 w-3.5" />
                </span>
              )}
              {event.skip_scraping && (
                <span className="text-amber-600" title="Scraping disabilitato">
                  <Shield className="h-3.5 w-3.5" />
                </span>
              )}
            </div>
          </div>

          <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-umbria-olive transition-colors flex-shrink-0" />
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// SIDE DRAWER
// ============================================================================

const EventDrawer = ({ event, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    start_date: event?.start_date || '',
    end_date: event?.end_date || '',
    manual_override: event?.manual_override || false,
    skip_scraping: event?.skip_scraping || false,
    override_reason: event?.override_reason || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (event) {
      setFormData({
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        manual_override: event.manual_override || false,
        skip_scraping: event.skip_scraping || false,
        override_reason: event.override_reason || ''
      });
    }
  }, [event]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/admin/events-freshness/${encodeURIComponent(event.id || event.title)}`,
        formData
      );

      toast.success(`Evento aggiornato: ${res.data.new_status}`);
      onSave();
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(err.response?.data?.detail || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  if (!event) return null;

  const statusConfig = EVENT_STATUS_CONFIG[event.event_status] || EVENT_STATUS_CONFIG.to_verify;
  const sourceConfig = SOURCE_TYPE_CONFIG[event.source_type] || SOURCE_TYPE_CONFIG.unknown;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div 
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 animate-slide-in-right overflow-y-auto"
        data-testid="event-drawer"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
          <h2 className="font-serif text-lg font-semibold text-umbria-charcoal truncate pr-4">
            {event.title}
          </h2>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="close-drawer"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* Current Status */}
          <div className="flex gap-2">
            <Badge className={`${statusConfig.color} border`}>
              {statusConfig.label}
            </Badge>
            <Badge className={`${sourceConfig.color} border`}>
              {sourceConfig.label}
            </Badge>
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Località</label>
            <p className="text-sm text-umbria-charcoal mt-1">{event.location || 'Non specificata'}</p>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                Data Inizio
              </label>
              <Input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData(f => ({ ...f, start_date: e.target.value }))}
                className="w-full"
                data-testid="input-start-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                Data Fine
              </label>
              <Input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData(f => ({ ...f, end_date: e.target.value }))}
                className="w-full"
                data-testid="input-end-date"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="space-y-4 pt-2">
            {/* Manual Override Toggle */}
            <div 
              className="flex items-center justify-between p-3 bg-purple-50/50 rounded-xl border border-purple-100 cursor-pointer hover:bg-purple-50 transition-colors"
              onClick={() => setFormData(f => ({ ...f, manual_override: !f.manual_override }))}
              data-testid="toggle-manual-override"
            >
              <div className="flex items-center gap-3">
                <Lock className={`h-5 w-5 ${formData.manual_override ? 'text-purple-600' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-umbria-charcoal">Override Manuale</p>
                  <p className="text-xs text-gray-500">Blocca aggiornamenti automatici</p>
                </div>
              </div>
              {formData.manual_override ? (
                <ToggleRight className="h-6 w-6 text-purple-600" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-400" />
              )}
            </div>

            {/* Skip Scraping Toggle */}
            <div 
              className="flex items-center justify-between p-3 bg-amber-50/50 rounded-xl border border-amber-100 cursor-pointer hover:bg-amber-50 transition-colors"
              onClick={() => setFormData(f => ({ ...f, skip_scraping: !f.skip_scraping }))}
              data-testid="toggle-skip-scraping"
            >
              <div className="flex items-center gap-3">
                <Shield className={`h-5 w-5 ${formData.skip_scraping ? 'text-amber-600' : 'text-gray-400'}`} />
                <div>
                  <p className="text-sm font-medium text-umbria-charcoal">Skip Scraping</p>
                  <p className="text-xs text-gray-500">Non includere in job automatici</p>
                </div>
              </div>
              {formData.skip_scraping ? (
                <ToggleRight className="h-6 w-6 text-amber-600" />
              ) : (
                <ToggleLeft className="h-6 w-6 text-gray-400" />
              )}
            </div>
          </div>

          {/* Override Reason */}
          {formData.manual_override && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1.5">
                Motivo Override
              </label>
              <textarea
                value={formData.override_reason}
                onChange={(e) => setFormData(f => ({ ...f, override_reason: e.target.value }))}
                placeholder="Es. Date confermate con organizzatore..."
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-umbria-olive/20 focus:border-umbria-olive resize-none"
                rows={2}
                data-testid="input-override-reason"
              />
            </div>
          )}

          {/* Source Info */}
          {event.source_url && (
            <div className="pt-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fonte</label>
              <a 
                href={event.source_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-umbria-olive hover:underline mt-1 block truncate"
              >
                {event.source_url}
              </a>
            </div>
          )}

          {/* Last Checked */}
          {event.last_checked && (
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ultimo Controllo</label>
              <p className="text-sm text-gray-600 mt-1">
                {new Date(event.last_checked).toLocaleString('it-IT')}
              </p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
            data-testid="btn-cancel"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-umbria-olive hover:bg-umbria-olive/90"
            data-testid="btn-save"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Salva
          </Button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function EventControlPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState(null); // null | 'upcoming' | 'to_verify' | 'manual_override'
  const [statusFilter, setStatusFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (activeFilter === 'upcoming') params.append('upcoming_only', 'true');
      if (activeFilter === 'to_verify') params.append('to_verify_only', 'true');
      if (activeFilter === 'manual_override') params.append('manual_override_only', 'true');
      if (statusFilter) params.append('event_status', statusFilter);
      if (sourceFilter) params.append('source_type', sourceFilter);
      params.append('limit', '100');

      const res = await axios.get(`${API}/admin/events-freshness/all?${params}`);
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to load events:', err);
      toast.error('Errore caricamento eventi');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeFilter, statusFilter, sourceFilter]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const recomputeStatuses = async () => {
    setRecomputing(true);
    try {
      const res = await axios.post(`${API}/admin/events-freshness/recompute-statuses`);
      toast.success(`Stati ricalcolati: ${res.data.upcoming} upcoming, ${res.data.ongoing} ongoing, ${res.data.past} past`);
      await loadEvents();
    } catch (err) {
      console.error('Recompute failed:', err);
      toast.error('Errore durante il ricalcolo degli stati');
    } finally {
      setRecomputing(false);
    }
  };

  // Stats
  const stats = {
    total: events.length,
    upcoming: events.filter(e => e.event_status === 'upcoming').length,
    ongoing: events.filter(e => e.event_status === 'ongoing').length,
    past: events.filter(e => e.event_status === 'past').length,
    to_verify: events.filter(e => e.event_status === 'to_verify').length,
    manual_override: events.filter(e => e.manual_override).length
  };

  return (
    <div className="space-y-6" data-testid="event-control-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-xl font-semibold text-umbria-charcoal">
            Gestione Eventi
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Stato temporale e affidabilità fonti
          </p>
        </div>
        
        <Button
          onClick={recomputeStatuses}
          disabled={recomputing}
          variant="outline"
          className="gap-2 self-start"
          data-testid="btn-recompute"
        >
          <RefreshCw className={`h-4 w-4 ${recomputing ? 'animate-spin' : ''}`} />
          Ricalcola Stati
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        <Card className="bg-white">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold text-umbria-charcoal">{stats.total}</p>
            <p className="text-xs text-gray-500">Totale</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold text-green-700">{stats.upcoming}</p>
            <p className="text-xs text-green-600">In programma</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold text-blue-700">{stats.ongoing}</p>
            <p className="text-xs text-blue-600">In corso</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold text-gray-600">{stats.past}</p>
            <p className="text-xs text-gray-500">Conclusi</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold text-amber-700">{stats.to_verify}</p>
            <p className="text-xs text-amber-600">Da verificare</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-100">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-semibold text-purple-700">{stats.manual_override}</p>
            <p className="text-xs text-purple-600">Override</p>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="space-y-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Cerca evento..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-events"
          />
        </div>

        {/* Quick Filters */}
        <div className="flex flex-wrap gap-2">
          <FilterPill 
            active={activeFilter === null && !statusFilter && !sourceFilter}
            onClick={() => { setActiveFilter(null); setStatusFilter(null); setSourceFilter(null); }}
          >
            Tutti
          </FilterPill>
          <FilterPill 
            active={activeFilter === 'upcoming'}
            onClick={() => setActiveFilter(activeFilter === 'upcoming' ? null : 'upcoming')}
            count={stats.upcoming}
          >
            <Calendar className="h-3.5 w-3.5" />
            In programma
          </FilterPill>
          <FilterPill 
            active={activeFilter === 'to_verify'}
            onClick={() => setActiveFilter(activeFilter === 'to_verify' ? null : 'to_verify')}
            count={stats.to_verify}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Da verificare
          </FilterPill>
          <FilterPill 
            active={activeFilter === 'manual_override'}
            onClick={() => setActiveFilter(activeFilter === 'manual_override' ? null : 'manual_override')}
            count={stats.manual_override}
          >
            <Lock className="h-3.5 w-3.5" />
            Override
          </FilterPill>
          
          {/* Source Type Dropdown */}
          <div className="relative group">
            <FilterPill active={!!sourceFilter}>
              <Filter className="h-3.5 w-3.5" />
              {sourceFilter ? SOURCE_TYPE_CONFIG[sourceFilter]?.label : 'Fonte'}
            </FilterPill>
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[150px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button 
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                onClick={() => setSourceFilter(null)}
              >
                Tutte le fonti
              </button>
              {Object.entries(SOURCE_TYPE_CONFIG).map(([key, config]) => (
                <button 
                  key={key}
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${sourceFilter === key ? 'bg-gray-50' : ''}`}
                  onClick={() => setSourceFilter(key)}
                >
                  <config.icon className="h-3.5 w-3.5" />
                  {config.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Events List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-umbria-olive" />
          </div>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nessun evento trovato</p>
            </CardContent>
          </Card>
        ) : (
          events.map((event) => (
            <EventCard 
              key={event.id || event.title}
              event={event}
              onClick={() => setSelectedEvent(event)}
            />
          ))
        )}
      </div>

      {/* Drawer */}
      {selectedEvent && (
        <EventDrawer
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onSave={() => {
            setSelectedEvent(null);
            loadEvents();
          }}
        />
      )}
    </div>
  );
}

export default EventControlPanel;
